import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import { HtmlOverlay3D } from '../HtmlOverlay3D'
import { TankDecorGroup3D } from '../TankDecorGroup3D'
import { HtmlOverlayPriority, Vec3 } from '@asset-viz/utils/helpers'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { useAssetVizTheme } from '@asset-viz/providers/theme'
import {
  GeckoAssetModel,
  GeckoCone,
  GeckoHorizontalTankShell,
  GeckoLaunchTube,
  GeckoLegacyCone,
  GeckoPressureVesselShell,
  GeckoTankFlatRoof,
  GeckoTankFloor,
  GeckoVerticalTankFoundation,
  GeckoVerticalTankShell,
} from 'types'

import { CardinalDirections, getCompassAnglePositionData } from './helpers'

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
`

const SecondaryTextContainer = styled.div<{ $color: string }>`
  color: ${({ $color }) => $color};
`
/**
 * Compass component that places the directions N (North), E (East), S (South),
 * W (West) at 90 degree intervals from the starting orientation and angles in between
 * based on the angle delta field.
 * Assumes that the component covers a full rotation of 360 degrees.
 *
 * @param assetModel the model of the asset that the compass is being applied on
 * @param getRotationPosition A function for calculating 3D compass position
 * @param radial boolean for expecting a radial compass which centers html tags
 *               rather than bottom positioning them
 * @param angleDeltaDegrees option to override the default 90 degree intervals with a different angle any angle that is a factor of 90 will
 *                   work and any angle that isn't a cardinal direction will be displayed as degrees
 */

export const Compass3D: React.FC<{
  getRotationPosition: (rotation: number) => Vec3
  assetModel: GeckoAssetModel
  componentModel:
    | GeckoTankFloor
    | GeckoTankFlatRoof
    | GeckoLaunchTube
    | GeckoLegacyCone
    | GeckoCone
    | GeckoHorizontalTankShell
    | GeckoVerticalTankShell
    | GeckoVerticalTankFoundation
    | GeckoPressureVesselShell
  radial?: boolean
  angleDeltaDegrees?: 1 | 2 | 3 | 5 | 6 | 9 | 10 | 15 | 18 | 30 | 45 | 90
  angleOffsetDegrees?: number
}> = ({
  getRotationPosition,
  assetModel,
  componentModel,
  radial,
  angleDeltaDegrees = 90,
  angleOffsetDegrees = 0,
}) => {
  const vizTheme = useAssetVizTheme()
  const { showCompass } = useAssetVizInternal()
  const showThisCompass = React.useMemo(() => {
    if (showCompass === true || showCompass === false) return showCompass
    return showCompass(componentModel)
  }, [showCompass, componentModel])
  const compassOrientation = useMemo(() => {
    return (
      componentModel.compassOrientation ?? assetModel.data.compass_orientation
    )
  }, [assetModel.data, componentModel])

  const { cardinalDirections, compassPositions } = useMemo(() => {
    if (compassOrientation === undefined) {
      return { cardinalDirections: [], compassPositions: [] }
    }
    return getCompassAnglePositionData(
      compassOrientation,
      getRotationPosition,
      assetModel.data.plant_north_compass_orientation,
      angleDeltaDegrees,
      angleOffsetDegrees
    )
  }, [
    compassOrientation,
    assetModel.data.plant_north_compass_orientation,
    getRotationPosition,
    angleDeltaDegrees,
    angleOffsetDegrees,
  ])

  return !showThisCompass
    ? null
    : compassPositions.map((position, i) => (
        <TankDecorGroup3D key={`compass_direction_${cardinalDirections[i]}`}>
          <HtmlOverlay3D
            anchor={radial ? 'center' : 'bottom'}
            x={position[0]}
            y={position[1]}
            z={position[2]}
            priority={HtmlOverlayPriority.RulerTickHigh}>
            <LabelContainer>
              {cardinalDirections[i] === CardinalDirections.North &&
              assetModel.data.plant_north_compass_orientation ? (
                <SecondaryTextContainer $color={vizTheme.secondaryText}>
                  Plant
                </SecondaryTextContainer>
              ) : null}
              <small>{cardinalDirections[i]}</small>
            </LabelContainer>
          </HtmlOverlay3D>
        </TankDecorGroup3D>
      ))
}
